import { useCallback, useState } from 'react'

import { useGet } from '../../../../hooks/apiHooks'
import { config } from '../../../Init/Init'
import { tokenizer } from '../../../../services/utils'
import { usePlayerStore } from '../../../../services/store'
import { useFormikContext } from 'formik'
import {
  cryptoPaymentErrorCodes,
  cryptoPaymentErrorMessages,
  depositFormFields,
  exchangeRateDefaultValues
} from '../../cryptoPaymentConstants'
import l10n from '../../../i18n/I18N'

export const useGetCryptoCurrencyExchangeRate = ({ setCryptoDepositError }) => {
  const playerStore = usePlayerStore()
  const playerUserId = playerStore.use.userId()

  const [exchangeRate, setExchangeRate] = useState(exchangeRateDefaultValues)

  const { GET, isLoading: isExchangeRateLoading } = useGet()
  const { setFieldError, setTouched, errors, setFieldValue } = useFormikContext()

  const handleCryptoCurrencyExchangeRateError = useCallback(
    errorData => {
      setExchangeRate(exchangeRateDefaultValues)

      setFieldValue(depositFormFields.coinsAmount, '')
      setFieldValue(depositFormFields.cryptoAmount, '')

      // set general error on deposit tab
      if (errorData instanceof Error) {
        return setCryptoDepositError(l10n.t[cryptoPaymentErrorMessages[cryptoPaymentErrorCodes.depositTabError]])
      }

      // handle other specific errors on deposit tab
      if (typeof errorData === 'number') {
        if (errorData === cryptoPaymentErrorCodes.bvnkError) {
          setTouched(
            {
              [depositFormFields.coinsAmount]: true,
              [depositFormFields.cryptoCurrencyCode]: true
            },
            false
          )

          setFieldError(
            depositFormFields.coinsAmount,
            l10n.t[cryptoPaymentErrorMessages[cryptoPaymentErrorCodes.bvnkError]]
          )
          setFieldError(
            depositFormFields.cryptoCurrencyCode,
            l10n.t[cryptoPaymentErrorMessages[cryptoPaymentErrorCodes.bvnkError]]
          )

          return
        }

        // handle other specific errors on deposit tab
        return setCryptoDepositError(l10n.t[cryptoPaymentErrorMessages[errorData]])
      }

      // fallback to general errors on deposit tab
      setCryptoDepositError(l10n.t[cryptoPaymentErrorMessages[cryptoPaymentErrorCodes.depositTabError]])
    },
    [setCryptoDepositError, setFieldError, setFieldValue, setTouched]
  )

  const handleCryptoCurrencyExchangeRateSuccess = useCallback(
    response => {
      if (response.errorCode) {
        return handleCryptoCurrencyExchangeRateError(cryptoPaymentErrorCodes.bvnkError)
      }

      if (response.rate) {
        setExchangeRate(response.rate)

        if (errors[depositFormFields.coinsAmount]) {
          setFieldError(depositFormFields.coinsAmount, undefined)
        }

        if (errors[depositFormFields.cryptoCurrencyCode]) {
          setFieldError(depositFormFields.cryptoCurrencyCode, undefined)
        }

        setCryptoDepositError(null)
      } else {
        throw 'No exchange rates found.'
      }
    },
    [errors, setCryptoDepositError, handleCryptoCurrencyExchangeRateError, setFieldError]
  )

  const getCryptoCurrencyExchangeRate = useCallback(
    chanelUuid => {
      GET(
        config.paymentsUrl +
          tokenizer(config.getCryptoExchangeRateUrl, {
            '{userId}': playerUserId,
            '{uuid}': chanelUuid
          })
      )
        .then(handleCryptoCurrencyExchangeRateSuccess)
        .catch(handleCryptoCurrencyExchangeRateError)
    },
    [GET, handleCryptoCurrencyExchangeRateError, handleCryptoCurrencyExchangeRateSuccess, playerUserId]
  )

  return { getCryptoCurrencyExchangeRate, exchangeRate, isExchangeRateLoading }
}
